import { useEffect, useState } from "react";
import { FaStar } from "react-icons/fa";
import { FiLoader, FiEdit3 } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { useAuth } from "../context/AuthContext";
import { useAuthModal } from "../context/AuthModalContext";
import { getProductReviews, createProductReview } from "../api/mintSpace";
import UsernameLink from "./UsernameLink";
import UserProfileLink from "./UserProfileLink";

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, secs] of units) {
    if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
  }
  return rtf.format(Math.round(diff), "second");
};

const TOP_COUNT = 4;

/**
 * Reviews block for the product details page: rating summary (average + per-star breakdown),
 * a list of top reviews, and an add-review form (rating + text). Reads/writes the public
 * /mint-space/:id/product/:productId/reviews endpoints. Self-contained and reusable elsewhere.
 */
const ProductReviews = ({ spaceId, productId, isDark = true, setToast }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();

  const [summary, setSummary] = useState({ average: 0, total: 0, breakdown: [] });
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formRating, setFormRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [formText, setFormText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Whether the reader may review: only buyers can, and never the owner.
  const [viewer, setViewer] = useState({ canReview: false, reason: "signedOut", myReview: null });

  // ----- theme tokens (match the site shell) -----
  const card = "border-separator-default bg-bg-raised";
  const muted = "text-label-secondary";
  const track = "bg-fill-primary";
  const emptyStar = isDark ? "text-slate-600" : "text-slate-300";
  const inputBase = "border-separator-default bg-bg-inset text-label-primary placeholder-label-tertiary focus:border-accent-default";

  const load = async () => {
    if (!spaceId || !productId) return;
    try {
      setLoading(true);
      const res = await getProductReviews(spaceId, productId, 50);
      if (res?.success) {
        setSummary({
          average: res.data.average || 0,
          total: res.data.total || 0,
          breakdown: res.data.breakdown || [],
        });
        setReviews(res.data.reviews || []);
        if (res.data.viewer) setViewer(res.data.viewer);
      }
    } catch (e) {
      // Non-fatal: just leave the section empty.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, productId]);

  const handleSubmit = async () => {
    if (!formRating) {
      setToast?.(t("mintSpace.selectRating") || "Please select a rating");
      return;
    }
    try {
      setSubmitting(true);
      const res = await createProductReview(spaceId, productId, {
        rating: formRating,
        text: formText.trim(),
      });
      if (res?.success) {
        setToast?.(t("mintSpace.reviewSubmitted") || "Review submitted");
        setFormOpen(false);
        setFormText("");
        setFormRating(0);
        await load();
      }
    } catch (e) {
      setToast?.(e?.message || t("mintSpace.reviewError") || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const Stars = ({ value = 0, size = "h-4 w-4" }) => (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <FaStar key={i} className={`${size} ${i <= value ? "text-status-warning" : emptyStar}`} />
      ))}
    </span>
  );

  const barColor = (star) =>
    star >= 4 ? "bg-green-500" : star === 3 ? "bg-amber-400" : star === 2 ? "bg-amber-500" : "bg-red-500";

  const Avatar = ({ author }) =>
    author?.avatar ? (
      <img src={author.avatar} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" />
    ) : (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-default text-subheadline-semibold text-accent-on">
        {(author?.name || "?").charAt(0).toUpperCase()}
      </span>
    );

  if (loading) {
    return (
      <div className="mt-12 flex justify-center py-8">
        <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
      </div>
    );
  }

  const shownReviews = showAll ? reviews : reviews.slice(0, TOP_COUNT);

  const openForm = () => {
    if (!formOpen && viewer.myReview) {
      setFormRating(viewer.myReview.rating);
      setFormText(viewer.myReview.text || "");
    }
    setFormOpen((o) => !o);
  };

  const writeReviewButton = !isAuthenticated ? (
    <button
      type="button"
      onClick={() => openAuthModal({ form: "signin" })}
      className={`inline-flex items-center gap-2 rounded-radius-sm border px-4 py-2 text-subheadline-semibold transition ${card} hover:border-accent-default`}
    >
      <FiEdit3 className="h-4 w-4" />
      {t("mintSpace.logInToReview") || "Log in to leave a review"}
    </button>
  ) : viewer.canReview ? (
    <button
      type="button"
      onClick={openForm}
      className="inline-flex items-center gap-2 rounded-radius-sm bg-accent-default px-4 py-2 text-subheadline-semibold text-accent-on transition hover:opacity-90"
    >
      <FiEdit3 className="h-4 w-4" />
      {viewer.myReview ? t("mintSpace.editReview") : t("mintSpace.writeReview") || "Write a review"}
    </button>
  ) : viewer.reason === "notBuyer" ? (
    <p className={`text-subheadline ${muted}`}>{t("mintSpace.onlyBuyersReview")}</p>
  ) : null;

  return (
    <section className="mt-12">
      <h2 className="text-title-1">{t("mintSpace.customerReviews") || "Customer reviews"}</h2>

      {/* Summary */}
      {summary.total > 0 ? (
        <div className={`mt-5 rounded-radius-md border p-6 ${card}`}>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
            <div className="flex flex-col items-center justify-center sm:w-44 sm:border-r sm:border-white/10">
              <div className="text-5xl font-extrabold leading-none">{summary.average}</div>
              <div className="mt-2">
                <Stars value={Math.round(summary.average)} size="h-5 w-5" />
              </div>
              <div className={`mt-2 text-subheadline ${muted}`}>
                {(t("mintSpace.ratingsCount") || "{count} ratings").replace("{count}", summary.total)}
              </div>
            </div>

            <div className="flex-1 space-y-2">
              {summary.breakdown.map((b) => (
                <div key={b.star} className="flex items-center gap-3">
                  <span className="flex w-8 items-center gap-1 text-subheadline font-medium">
                    {b.star}
                    <FaStar
                      className={`h-3.5 w-3.5 ${
                        b.star >= 4
                          ? "text-status-success"
                          : b.star >= 2
                          ? "text-status-warning"
                          : "text-status-danger"
                      }`}
                    />
                  </span>
                  <div className={`h-2.5 flex-1 overflow-hidden rounded-full ${track}`}>
                    <div
                      className={`h-full rounded-full ${barColor(b.star)}`}
                      style={{ width: `${b.percent}%` }}
                    />
                  </div>
                  <span className={`w-24 text-right text-subheadline ${muted}`}>
                    {b.percent}% ({b.count})
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className={`mt-4 text-subheadline ${muted}`}>
          {t("mintSpace.noProductReviewsYet")}
        </p>
      )}

      {/* Add review */}
      <div className="mt-6">
        {writeReviewButton}
        {formOpen && viewer.canReview && (
          <div className={`mt-4 rounded-radius-md border p-5 ${card}`}>
            <p className="text-subheadline-semibold">{t("mintSpace.yourRating") || "Your rating"}</p>
            <div className="mt-2 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  type="button"
                  onMouseEnter={() => setHoverRating(i)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setFormRating(i)}
                  className="p-0.5"
                  aria-label={`${i} stars`}
                >
                  <FaStar
                    className={`h-6 w-6 transition ${
                      (hoverRating || formRating) >= i ? "text-status-warning" : emptyStar
                    }`}
                  />
                </button>
              ))}
            </div>
            <textarea
              value={formText}
              onChange={(e) => setFormText(e.target.value.slice(0, 1000))}
              rows={3}
              placeholder={t("mintSpace.reviewPlaceholder") || "Share your experience…"}
              className={`mt-4 w-full resize-y rounded-radius-sm border px-4 py-3 text-subheadline outline-none transition ${inputBase}`}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className={`rounded-radius-sm border px-4 py-2 text-subheadline-semibold transition ${card} hover:border-accent-default`}
              >
                {t("common.cancel") || "Cancel"}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-radius-sm bg-accent-default px-5 py-2 text-subheadline-semibold text-accent-on transition hover:opacity-90 disabled:opacity-50"
              >
                {submitting && <FiLoader className="h-4 w-4 animate-spin" />}
                {t("mintSpace.submitReview") || "Submit review"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Top reviews */}
      {reviews.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center justify-between">
            <h3 className="text-title-2">{t("mintSpace.topReviews") || "Top reviews"}</h3>
            {reviews.length > TOP_COUNT && (
              <button
                type="button"
                onClick={() => setShowAll((s) => !s)}
                className={`text-subheadline-semibold transition hover:text-accent-text ${muted}`}
              >
                {showAll
                  ? t("mintSpace.showLess") || "Show less"
                  : t("mintSpace.seeAllReviews") || "See all reviews"}
              </button>
            )}
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {shownReviews.map((r) => (
              <div key={r._id} className={`rounded-radius-md border p-5 ${card}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <UserProfileLink
                      user={r.author}
                      avatarSize="lg"
                      isDark={isDark}
                      nameClassName="truncate font-bold"
                      fallback={r.author?.name || "—"}
                    />
                    {r.author?.username && (
                      <UsernameLink user={r.author} className={`mt-0.5 block truncate text-subheadline ${muted}`} />
                    )}
                  </div>
                  <span className={`shrink-0 text-subheadline ${muted}`}>{timeAgo(r.createdAt)}</span>
                </div>
                <div className="mt-3">
                  <Stars value={r.rating} />
                </div>
                {r.text && <p className="mt-3 text-subheadline leading-relaxed">{r.text}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductReviews;
